import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  FOCUS_DURATIONS,
  type FocusDuration,
  MAX_WINDOW_ENERGY,
  WINDOW_HOURS,
  GRACE_PERIOD_MINUTES,
  ENERGY_INTERVAL_MINUTES,
  TRAVEL_COST,
  TRAVEL_TIME_MINUTES,
} from '@/data/constants'
import { HUNTS, type HuntType } from '@/data/hunts'
import { rollEcho, type HuntResult } from '@/utils/hunting'

interface FocusSession {
  duration: FocusDuration
  startedAt: string
}

interface EnergySession {
  at: string
  earned: number
  spent: number
}

interface Activity {
  type: 'travel' | 'hunt'
  startedAt: string
  dest?: string
  huntType?: HuntType
}

interface PendingResult {
  type: 'travel' | 'hunt'
  place?: string
  huntResult?: HuntResult
}

// Activity log entry types
type ActivityLogEntryBase = {
  id: string
  timestamp: string
}

type FocusLogData = {
  type: 'focus'
  duration: FocusDuration
  actualMinutes: number
  energyEarned: number
  decay: number
  wasEarlyStop: boolean
}

type HuntLogData = {
  type: 'hunt'
  huntType: HuntType
  energySpent: number
  place: string
  success: boolean
  echoId?: string
  isGlimmering?: boolean
  isNewDiscovery?: boolean
}

type TravelLogData = {
  type: 'travel'
  energySpent: number
  fromPlace: string
  toPlace: string
}

export type FocusLogEntry = ActivityLogEntryBase & FocusLogData
export type HuntLogEntry = ActivityLogEntryBase & HuntLogData
export type TravelLogEntry = ActivityLogEntryBase & TravelLogData

export type ActivityLogEntry = FocusLogEntry | HuntLogEntry | TravelLogEntry
export type ActivityLogInput = FocusLogData | HuntLogData | TravelLogData

interface GameState {
  // Core state
  focusSession: FocusSession | null
  sessions: EnergySession[]
  activity: Activity | null
  place: string
  discovered: string[]
  glimmering: string[]
  sightings: Record<string, number>
  pending: PendingResult | null
  lastReset: string
  activityLog: ActivityLogEntry[]

  // Computed
  getEnergy: () => { total: number; windowRemaining: number }
  isPlaceUnlocked: (placeId: string) => boolean

  // Actions
  startFocus: (duration: FocusDuration) => void
  stopFocus: () => { earned: number; decay: number }
  startHunt: (huntType: HuntType) => boolean
  startTravel: (destPlaceId: string) => boolean
  cancelActivity: () => void
  checkActivity: () => PendingResult | null
  clearPending: () => void
  spendEnergy: (amount: number) => void
  resetProgress: () => void
  reconcile: () => void
  addDebugEnergy: (amount: number) => void
  logActivity: (entry: ActivityLogInput) => void
}

function minutesSince(iso: string | null): number {
  if (!iso) return Infinity
  return (Date.now() - new Date(iso).getTime()) / 60000
}

const initialState = {
  focusSession: null,
  sessions: [],
  activity: null,
  place: 'embuns-clearing',
  discovered: [],
  glimmering: [],
  sightings: {},
  pending: null,
  lastReset: new Date().toDateString(),
  activityLog: [],
}

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      ...initialState,

      getEnergy: () => {
        const { sessions } = get()
        const now = Date.now()
        const windowStart = now - WINDOW_HOURS * 60 * 60 * 1000

        let windowUsed = 0
        let total = 0

        for (const session of sessions) {
          const sessionTime = new Date(session.at).getTime()
          if (sessionTime > windowStart) {
            windowUsed += session.earned
          }
          total += session.earned - session.spent
        }

        return {
          total: Math.max(0, total),
          windowRemaining: Math.max(0, MAX_WINDOW_ENERGY - windowUsed),
        }
      },

      isPlaceUnlocked: (placeId: string) => {
        const { discovered } = get()
        // For MVP, simple unlock logic
        if (placeId === 'embuns-clearing') return true
        if (placeId === 'tall-grass-way') return discovered.length >= 3
        return false
      },

      startFocus: (duration: FocusDuration) => {
        const state = get()
        // Cancel any ongoing activity
        if (state.activity) {
          set({ activity: null })
        }
        set({
          focusSession: {
            duration,
            startedAt: new Date().toISOString(),
          },
        })
      },

      stopFocus: () => {
        const { focusSession, sessions, getEnergy, logActivity } = get()
        if (!focusSession) return { earned: 0, decay: 0 }

        const elapsed = minutesSince(focusSession.startedAt)
        const target = focusSession.duration
        const baseEnergy = FOCUS_DURATIONS[target]
        const wasEarlyStop = elapsed < target

        let earned = 0
        let decay = 0

        if (elapsed >= target) {
          // Completed
          const overTime = elapsed - target
          if (overTime > GRACE_PERIOD_MINUTES) {
            decay = Math.floor(overTime - GRACE_PERIOD_MINUTES)
            earned = Math.max(0, baseEnergy - decay)
          } else {
            earned = baseEnergy
          }
        } else {
          // Early stop - pro-rate to lowest 5-minute interval
          const completedIntervals = Math.floor(elapsed / ENERGY_INTERVAL_MINUTES)
          earned = completedIntervals  // 1 energy per 5-minute interval
        }

        // Cap by window remaining
        const { windowRemaining } = getEnergy()
        earned = Math.min(earned, windowRemaining)

        // Log the focus session
        logActivity({
          type: 'focus',
          duration: target,
          actualMinutes: Math.floor(elapsed),
          energyEarned: earned,
          decay,
          wasEarlyStop,
        })

        if (earned > 0) {
          set({
            focusSession: null,
            sessions: [
              ...sessions,
              {
                at: new Date().toISOString(),
                earned,
                spent: 0,
              },
            ],
          })
        } else {
          set({ focusSession: null })
        }

        return { earned, decay }
      },

      startHunt: (huntType: HuntType) => {
        const state = get()
        if (state.focusSession || state.activity) return false

        const hunt = HUNTS[huntType]
        const { total } = state.getEnergy()

        if (total < hunt.energy) return false

        state.spendEnergy(hunt.energy)
        set({
          activity: {
            type: 'hunt',
            startedAt: new Date().toISOString(),
            huntType,
          },
        })
        return true
      },

      startTravel: (destPlaceId: string) => {
        const state = get()
        if (state.focusSession || state.activity) return false

        const { total } = state.getEnergy()
        if (total < TRAVEL_COST) return false

        if (!state.isPlaceUnlocked(destPlaceId)) return false

        state.spendEnergy(TRAVEL_COST)
        set({
          activity: {
            type: 'travel',
            startedAt: new Date().toISOString(),
            dest: destPlaceId,
          },
        })
        return true
      },

      cancelActivity: () => {
        // Energy already spent, just cancel
        set({ activity: null })
      },

      checkActivity: () => {
        const { activity, place, pending, logActivity } = get()

        if (pending) return pending
        if (!activity) return null

        const elapsed = minutesSince(activity.startedAt)
        const required =
          activity.type === 'travel'
            ? TRAVEL_TIME_MINUTES
            : HUNTS[activity.huntType!].timeMinutes

        if (elapsed < required) return null

        // Activity complete
        let result: PendingResult

        if (activity.type === 'travel') {
          result = { type: 'travel', place: activity.dest }

          // Log travel completion
          logActivity({
            type: 'travel',
            energySpent: TRAVEL_COST,
            fromPlace: place,
            toPlace: activity.dest!,
          })

          set({
            activity: null,
            place: activity.dest!,
            pending: result,
          })
        } else {
          const huntResult = rollEcho(activity.huntType!, place)
          result = { type: 'hunt', huntResult }

          const updates: Partial<GameState> = {
            activity: null,
            pending: result,
          }

          const state = get()
          const isNewDiscovery =
            huntResult.success &&
            huntResult.echo &&
            !state.discovered.includes(huntResult.echo.id)

          if (huntResult.success && huntResult.echo) {
            const echoId = huntResult.echo.id

            if (isNewDiscovery) {
              updates.discovered = [...state.discovered, echoId]
            }
            if (huntResult.isGlimmering && !state.glimmering.includes(echoId)) {
              updates.glimmering = [...state.glimmering, echoId]
            }
            updates.sightings = {
              ...state.sightings,
              [echoId]: (state.sightings[echoId] || 0) + 1,
            }
          }

          // Log hunt completion
          const hunt = HUNTS[activity.huntType!]
          logActivity({
            type: 'hunt',
            huntType: activity.huntType!,
            energySpent: hunt.energy,
            place,
            success: huntResult.success,
            echoId: huntResult.echo?.id,
            isGlimmering: huntResult.isGlimmering,
            isNewDiscovery,
          })

          set(updates as GameState)
        }

        return result
      },

      clearPending: () => {
        set({ pending: null })
      },

      spendEnergy: (amount: number) => {
        const { sessions } = get()
        let remaining = amount
        const updated = [...sessions]

        for (let i = updated.length - 1; i >= 0 && remaining > 0; i--) {
          const session = updated[i]!
          const available = session.earned - session.spent
          const spend = Math.min(available, remaining)
          updated[i] = { ...session, spent: session.spent + spend }
          remaining -= spend
        }

        set({ sessions: updated })
      },

      resetProgress: () => {
        set({
          ...initialState,
          lastReset: new Date().toDateString(),
        })
      },

      reconcile: () => {
        const state = get()
        const today = new Date().toDateString()

        if (state.lastReset !== today) {
          set({
            sessions: [],
            lastReset: today,
          })
        }

        // Check for completed activities
        state.checkActivity()
      },

      addDebugEnergy: (amount: number) => {
        const { sessions } = get()
        set({
          sessions: [
            ...sessions,
            {
              at: new Date().toISOString(),
              earned: amount,
              spent: 0,
            },
          ],
        })
      },

      logActivity: (entry) => {
        const { activityLog } = get()
        const fullEntry: ActivityLogEntry = {
          ...entry,
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          timestamp: new Date().toISOString(),
        } as ActivityLogEntry

        set({
          activityLog: [fullEntry, ...activityLog].slice(0, 100),
        })
      },
    }),
    {
      name: 'echowilds-game',
    }
  )
)
